import { useState, type SyntheticEvent } from 'react'
import { CheckIcon, PencilIcon, PlusIcon, XIcon } from 'lucide-react'
import { toast } from 'sonner'
import {
  formatDecimalHours,
  formatDuration,
  parseBudgetHours,
  type Project
} from '@coneno/c-sheet-shared'
import { AppSelect } from '@/components/forms/app-select'
import { BudgetField } from '@/components/forms/budget-field'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { useCustomers } from '@/hooks/use-customers'
import { useCreateProject, useUpdateProject } from '@/hooks/use-projects'
import { useCreateTask, useTasks, useUpdateTask } from '@/hooks/use-tasks'

function budgetInput(minutes: number | null): string {
  return minutes === null ? '' : formatDuration(minutes)
}

function ProjectForm({
  project,
  onSaved,
  onCancel
}: {
  project: Project | null
  onSaved(project: Project): void
  onCancel(): void
}): React.JSX.Element {
  const customers = useCustomers()
  const createProject = useCreateProject()
  const updateProject = useUpdateProject()
  const [name, setName] = useState(project?.name ?? '')
  const [customerId, setCustomerId] = useState(project?.customerId ?? '')
  const [code, setCode] = useState(project?.code ?? '')
  const [budget, setBudget] = useState(budgetInput(project?.budgetMinutes ?? null))
  const [billable, setBillable] = useState(project?.billable ?? true)
  const [active, setActive] = useState(project?.active ?? true)
  const pending = createProject.isPending || updateProject.isPending
  const error = createProject.error ?? updateProject.error
  const budgetMinutes = parseBudgetHours(budget)
  const canSave = Boolean(name.trim()) && budgetMinutes !== undefined && !pending

  function submit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): void {
    event.preventDefault()
    if (!canSave) return
    const input = {
      name: name.trim(),
      customerId: customerId || null,
      code: code.trim() || null,
      budgetMinutes,
      billable
    }
    if (!project) {
      createProject.mutate(input, {
        onSuccess: (created) => {
          toast(`Project “${created.name}” added`)
          onSaved(created)
        }
      })
      return
    }
    updateProject.mutate(
      { id: project.id, input: { ...input, active } },
      {
        onSuccess: (updated) => {
          if (project.active && !updated.active) {
            toast(`Project “${updated.name}” deactivated`, {
              action: {
                label: 'Undo',
                onClick: () => updateProject.mutate({ id: updated.id, input: { active: true } })
              }
            })
          } else {
            toast('Project saved')
          }
          onSaved(updated)
        }
      }
    )
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={submit}>
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="project-name">Name</FieldLabel>
          <Input
            id="project-name"
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. Website relaunch"
            disabled={pending}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="project-client">Client</FieldLabel>
          <AppSelect
            id="project-client"
            value={customerId}
            onValueChange={setCustomerId}
            disabled={pending}
            options={[
              { value: '', label: 'Internal (no client)' },
              ...(customers.data
                ?.filter((customer) => customer.active || customer.id === customerId)
                .map((customer) => ({ value: customer.id, label: customer.name })) ?? [])
            ]}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="project-code">
            Code <span className="text-muted-foreground">(optional)</span>
          </FieldLabel>
          <Input
            id="project-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="e.g. WEB-26"
            disabled={pending}
          />
        </Field>
        <BudgetField id="project-budget" value={budget} onChange={setBudget} disabled={pending} />
        <Field orientation="horizontal">
          <Switch
            id="project-billable"
            nativeButton
            render={<button type="button" />}
            checked={billable}
            onCheckedChange={setBillable}
            disabled={pending}
          />
          <FieldLabel htmlFor="project-billable">Billable by default</FieldLabel>
        </Field>
        {project && (
          <Field orientation="horizontal">
            <Switch
              id="project-active"
              nativeButton
              render={<button type="button" />}
              checked={active}
              onCheckedChange={setActive}
              disabled={pending}
            />
            <FieldLabel htmlFor="project-active">
              Active{' '}
              <span className="font-normal text-muted-foreground">
                (inactive projects stay on past entries)
              </span>
            </FieldLabel>
          </Field>
        )}
      </FieldGroup>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error.message}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSave}>
          {pending ? 'Saving…' : project ? 'Save project' : 'Add project'}
        </Button>
      </div>
    </form>
  )
}

function TaskList({ projectId }: { projectId: string }): React.JSX.Element {
  const tasks = useTasks(projectId)
  const createTask = useCreateTask()
  const updateTask = useUpdateTask()
  const [name, setName] = useState('')
  const [budget, setBudget] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [editingBudget, setEditingBudget] = useState('')
  const error = tasks.error ?? createTask.error ?? updateTask.error

  function addTask(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): void {
    event.preventDefault()
    const budgetMinutes = parseBudgetHours(budget)
    if (!name.trim() || budgetMinutes === undefined) return
    createTask.mutate(
      { projectId, name: name.trim(), budgetMinutes },
      {
        onSuccess: () => {
          setName('')
          setBudget('')
        }
      }
    )
  }

  function saveTask(id: string): void {
    const budgetMinutes = parseBudgetHours(editingBudget)
    if (!editingName.trim() || budgetMinutes === undefined) return
    updateTask.mutate(
      { id, input: { name: editingName.trim(), budgetMinutes } },
      { onSuccess: () => setEditingId(null) }
    )
  }

  return (
    <section className="flex flex-col gap-3" aria-labelledby="project-tasks">
      <h3 id="project-tasks" className="font-medium">
        Tasks
      </h3>
      {tasks.isPending ? (
        <p className="text-sm text-muted-foreground">Loading tasks…</p>
      ) : tasks.data?.length ? (
        <ul className="flex flex-col divide-y rounded-md border">
          {tasks.data.map((task) => (
            <li key={task.id} className="flex items-center gap-2 px-3 py-2">
              {editingId === task.id ? (
                <>
                  <div className="grid min-w-0 flex-1 grid-cols-[1fr_6rem] gap-2">
                    <Input
                      aria-label={`Name for ${task.name}`}
                      value={editingName}
                      autoFocus
                      onChange={(event) => setEditingName(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') saveTask(task.id)
                        if (event.key === 'Escape') setEditingId(null)
                      }}
                    />
                    <Input
                      aria-label={`Hour budget for ${task.name}`}
                      placeholder="Budget"
                      value={editingBudget}
                      aria-invalid={parseBudgetHours(editingBudget) === undefined}
                      onChange={(event) => setEditingBudget(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') saveTask(task.id)
                        if (event.key === 'Escape') setEditingId(null)
                      }}
                    />
                  </div>
                  <Button
                    size="icon-sm"
                    aria-label={`Save ${task.name}`}
                    onClick={() => saveTask(task.id)}
                    disabled={
                      updateTask.isPending ||
                      !editingName.trim() ||
                      parseBudgetHours(editingBudget) === undefined
                    }
                  >
                    <CheckIcon />
                  </Button>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label="Cancel editing"
                    onClick={() => setEditingId(null)}
                  >
                    <XIcon />
                  </Button>
                </>
              ) : (
                <>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{task.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {task.budgetMinutes === null
                        ? 'No budget'
                        : `${formatDecimalHours(task.budgetMinutes)} h budget`}
                    </p>
                  </div>
                  {!task.active && <Badge variant="secondary">Inactive</Badge>}
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label={`Edit ${task.name}`}
                    onClick={() => {
                      setEditingId(task.id)
                      setEditingName(task.name)
                      setEditingBudget(budgetInput(task.budgetMinutes))
                    }}
                  >
                    <PencilIcon />
                  </Button>
                  <Switch
                    size="sm"
                    nativeButton
                    render={<button type="button" />}
                    aria-label={`${task.name} active`}
                    checked={task.active}
                    disabled={updateTask.isPending}
                    onCheckedChange={(active) =>
                      updateTask.mutate({ id: task.id, input: { active } })
                    }
                  />
                </>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          No tasks yet. Time can also be recorded without one.
        </p>
      )}
      <form className="flex items-start gap-2" onSubmit={addTask}>
        <Input
          aria-label="New task name"
          placeholder="New task, e.g. Development"
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={createTask.isPending}
        />
        <Input
          aria-label="New task hour budget"
          placeholder="Budget"
          className="w-24"
          value={budget}
          aria-invalid={parseBudgetHours(budget) === undefined}
          onChange={(event) => setBudget(event.target.value)}
          disabled={createTask.isPending}
        />
        <Button
          type="submit"
          variant="outline"
          aria-label="Add task"
          disabled={createTask.isPending || !name.trim() || parseBudgetHours(budget) === undefined}
        >
          <PlusIcon />
        </Button>
      </form>
      {error && <p className="text-sm text-destructive">{error.message}</p>}
    </section>
  )
}

/** Side panel for adding a project or editing one together with its tasks. */
export function ProjectPanel({
  open,
  project,
  onOpenChange,
  onCreated
}: {
  open: boolean
  project: Project | null
  onOpenChange(open: boolean): void
  onCreated(project: Project): void
}): React.JSX.Element {
  // Keep showing the last project while the panel slides closed, instead of the empty form.
  const [shown, setShown] = useState(project)
  if (open && project !== shown) setShown(project)
  const displayed = open ? project : shown

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg data-[side=right]:sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{displayed ? displayed.name : 'New project'}</SheetTitle>
          <SheetDescription>
            {displayed
              ? 'Changes apply to new entries; past entries keep their billable setting.'
              : 'Client, code, budget and billable status can be changed later.'}
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-6 px-4 pb-6">
          <ProjectForm
            key={displayed?.id ?? 'new'}
            project={displayed}
            onCancel={() => onOpenChange(false)}
            onSaved={(saved) => (displayed ? onOpenChange(false) : onCreated(saved))}
          />
          {displayed && (
            <>
              <Separator />
              <TaskList projectId={displayed.id} />
            </>
          )}
        </div>
        <SheetFooter className="sr-only" />
      </SheetContent>
    </Sheet>
  )
}
